import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Board, CURRENCY, ShippingAddress } from "./uploadedBoard";
import { Coupon } from "./models/users";
import {
  InvoiceSummary,
  OrderState,
  PaymentRecord,
  PaymentState,
} from "../components/Orders/constants";

export interface ShoppingCartItem {
  board: Board;
}

export interface PickupInfo {
  company_name: string | null;
  email: string;
  name: string;
  phone_number: string;
}

export interface ShippingRate {
  provider: string;
  provider_logo_link: string;
  service_level_name: string;
  service_level_token: string;
  amount: number;
  currency: CURRENCY;
  estimated_days: number;
  shippo_rate_object_id: string;
  tracking_url: string | null;
  shippo_label_url: string | null;
}

export interface PaymentDetails {
  card_type: string | null;
  last_4_digits: string | null;
  amount: number;
  currency: CURRENCY;
}

export interface ShoppingCartOrder {
  state: OrderState;
  items: ShoppingCartItem[];
  boards_total: number;
  tax: number | null;
  tax_exempt: boolean;
  tariff: number;
  discounts: number | null;
  order_total: number;
  date_accepted: string | null;
  date_paid_in_full: string | null;
  datetime_refunded: string | null;
  stripe_payment_intent_id: string | null;
  stripe_client_secret: string | null;
  currency: CURRENCY;
  shipping_address: ShippingAddress | null;
  num_shipments: number;
  shipping_rate_amount: number;
  shipping_rate: ShippingRate | null;
  pickup_info: PickupInfo | null;
  coupons_applied: Coupon[];
  payment_details: PaymentDetails | null;
  was_tracked: boolean | null;
  next_order_id: string | null;
  payment_records: PaymentRecord[] | null;
  payment_state: PaymentState | null;
  invoice_summary: InvoiceSummary;
}

export enum PaymentMethod {
  NONE_NEEDED = "None needed",
  CARD = "Card",
  PURCHASE_ORDER = "Purchase order",
}

export interface CardPaymentContext {
  paymentInfoComplete: boolean;
}

export interface PurchaseOrderPaymentContext {
  purchaseOrderNumber: string;
  sendInvoiceTo: string;
  hasPurchaseOrderFile: boolean;
}

export interface PaymentContext {
  selectedPaymentMethod: PaymentMethod;
  cardPaymentContext: CardPaymentContext | null;
  purchaseOrderPaymentContext: PurchaseOrderPaymentContext | null;
}

interface ShoppingCartOrderStore {
  shoppingCartOrder: ShoppingCartOrder | null;
  updating: boolean;
  errored: boolean;
  paymentContext: PaymentContext;
  checkoutError: string | null;
  notFound: boolean;
  checkingOut: boolean;
}

const defaultCardPaymentContext = {
  paymentInfoComplete: false,
};

const defaultPaymentContext: PaymentContext = {
  selectedPaymentMethod: PaymentMethod.CARD,
  cardPaymentContext: defaultCardPaymentContext,
  purchaseOrderPaymentContext: null,
};

const defaultCartState = {
  shoppingCartOrder: null,
  paymentContext: defaultPaymentContext,
  updating: false,
  errored: false,
  checkoutError: null,
  notFound: false,
  checkingOut: false,
};
export const shoppingCartOrderSlice = createSlice({
  name: "shoppingCartOrder",
  initialState: defaultCartState as ShoppingCartOrderStore,
  reducers: {
    resetCart: () => defaultCartState,
    setNotFound: (state) => {
      state.notFound = true;
    },
    setShoppingCart: (
      state,
      action: PayloadAction<{ shoppingCartOrder: ShoppingCartOrder }>,
    ) => {
      state.shoppingCartOrder = action.payload.shoppingCartOrder;
      state.notFound = false;
    },
    setCartActionError: (
      state,
      action: PayloadAction<{ errored: boolean }>,
    ) => {
      state.errored = action.payload.errored;
      state.checkingOut = false;
    },
    setCheckoutError: (
      state,
      action: PayloadAction<{ error: string | null }>,
    ) => {
      state.checkoutError = action.payload.error;
    },
    setUpdatingShoppingCart: (
      state,
      action: PayloadAction<{ updating: boolean }>,
    ) => {
      state.updating = action.payload.updating;
    },
    setPaymentMethod: (
      state,
      action: PayloadAction<{ paymentMethod: PaymentMethod }>,
    ) => {
      state.paymentContext.selectedPaymentMethod = action.payload.paymentMethod;
      if (action.payload.paymentMethod === PaymentMethod.CARD) {
        state.paymentContext.cardPaymentContext = defaultCardPaymentContext;
      } else if (
        action.payload.paymentMethod === PaymentMethod.PURCHASE_ORDER
      ) {
        state.paymentContext.purchaseOrderPaymentContext = {
          purchaseOrderNumber: "",
          sendInvoiceTo: "",
          hasPurchaseOrderFile: false,
        };
      } else {
        state.paymentContext.cardPaymentContext = null;
        state.paymentContext.purchaseOrderPaymentContext = null;
      }
    },
    setCardPaymentContext: (
      state,
      action: PayloadAction<CardPaymentContext | null>,
    ) => {
      state.paymentContext.cardPaymentContext = action.payload;
    },
    setPurchaseOrderPaymentContext: (
      state,
      action: PayloadAction<PurchaseOrderPaymentContext | null>,
    ) => {
      state.paymentContext.purchaseOrderPaymentContext = action.payload;
    },
    setCheckingOut: (
      state,
      action: PayloadAction<{ checkingOut: boolean }>,
    ) => {
      state.checkingOut = action.payload.checkingOut;
    },
  },
});

export const {
  resetCart,
  setNotFound,
  setShoppingCart,
  setCartActionError,
  setCheckoutError,
  setUpdatingShoppingCart,
  setPaymentMethod,
  setCardPaymentContext,
  setPurchaseOrderPaymentContext,
  setCheckingOut,
} = shoppingCartOrderSlice.actions;

export default shoppingCartOrderSlice.reducer;
